import { Zone } from 'pages/Zones/types';
import * as Yup from 'yup';

import { MODULE_LAYOUTS } from 'components/Zones/v2/types';

const HTML_ERROR_TITLE = 'HTML Input Error';

// Internal helpers
const basicStringValidator = (field?: string, fieldName?: string) => {
  if (!field) return `Enter ${fieldName ?? 'value'}`;
  if (field.length > 40) return `Enter a ${fieldName ?? 'value'} that is 40 characters or less`;

  return '';
};

const basicNumberValidator = (field?: number, fieldName?: string) => {
  if (!field) return `Enter ${fieldName ?? 'value'}`;
  return '';
};

const specialCharacterValidator = (field?: string, fieldName?: string) => {
  if (!field || !/[\w-]+/.test(field))
    return `Enter a unique ${
      fieldName ?? 'value'
    } that only contains letters, numbers, dashes, and underscores. Avoid using any special characters.`;
  return '';
};

const emptyArrayValidator = <T>(arr?: T[], fieldName?: string) => {
  if (!arr?.length) return `Add at least one ${fieldName ?? 'value'}`;
  return '';
};

const refIdUniquenessValidator = (id?: string, refId?: string, zones?: Zone[]) => {
  if (refId && zones) {
    if (zones.some((zone) => zone.id !== id && zone.referenceID === refId)) {
      return 'Reference identifier is not unique';
    } else {
      return '';
    }
  }

  return '';
};
// Schemas
export const zoneAttributesSchema = Yup.object().shape({
  name: Yup.string()
    .required('Enter name')
    .test('basic-string', '', function (value) {
      const error = basicStringValidator(value, 'name');
      return error === '' || this.createError({ message: error });
    }),
  id: Yup.string(),
  refId: Yup.string()
    .required('Enter reference identifier')
    .test('basic-string', '', function (value) {
      const error = basicStringValidator(value, 'reference identifier');
      return error === '' || this.createError({ message: error });
    })
    .test('special-character', '', function (value) {
      const error = specialCharacterValidator(value, 'reference identifier');
      return error === '' || this.createError({ message: error });
    })
    .test('unique-refId', '', function (value) {
      const { id, zones } = this.parent as { id?: string; zones?: Zone[] };
      const error = refIdUniquenessValidator(id, value, zones);
      return error === '' || this.createError({ message: error });
    }),
  labels: Yup.array().test('not-empty-array', '', function (value) {
    const error = emptyArrayValidator<string>(value, 'label');
    return error === '' || this.createError({ message: error });
  }),
});
export const handleValidateZoneAttributes = async (
  name?: string,
  refId?: string,
  labels?: string[],
  id?: string,
  zones?: Zone[],
) => {
  try {
    await zoneAttributesSchema.validate(
      {
        name,
        refId,
        labels,
        id,
        zones,
      },
      { abortEarly: false },
    ); // abortEarly: false collects all errors
    return {};
  } catch (err) {
    const newErrors: { [key: string]: string } = {};
    if (err instanceof Yup.ValidationError && Array.isArray(err.inner)) {
      err.inner.forEach((error) => {
        if (error.path !== undefined) {
          newErrors[error.path] = error.message;
        }
      });
    }
    return newErrors;
  }
};
// Exports
export const validateZoneAttributes = (
  name?: string,
  refId?: string,
  labels?: string[],
  id?: string,
  zones?: Zone[],
) => {
  return {
    name: basicStringValidator(name, 'name'),
    refId:
      basicStringValidator(refId, 'reference identifier') ||
      specialCharacterValidator(refId, 'reference identifier') ||
      refIdUniquenessValidator(id, refId, zones),
    labels: emptyArrayValidator<string>(labels, 'label'),
  };
};

// TODO: add `layout` field to this validator
export const validateModuleInfo = (layout: MODULE_LAYOUTS, name?: string, height?: number, pageSize?: number) => {
  return {
    name: basicStringValidator(name, 'name'),
    height: basicNumberValidator(height, 'height'),
    layout: !layout ? 'Select a layout' : '',
    pageSize: basicNumberValidator(pageSize, 'page size'),
  };
};

export const validateModuleLayout = async (layoutHTML?: string) => {
  return {
    layoutHTML: await htmlValidator(layoutHTML),
  };
};

// ToDo: Replace with actual validation
export function htmlValidator(code?: string) {
  if (!code) {
    return `${HTML_ERROR_TITLE}: the HTML box is empty`;
  }

  return '';
}

/**
 * Validates the content limit for labelManager.
 * @param count - The number of labels to validate.
 * @returns An error message if the limit is exceeded, otherwise an empty string.
 */
export function validateLabelManagerContentLimit(count?: number | null): string {
  return basicNumberValidator(count ?? undefined, 'count is null or undefined');
}
